import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { StatusBadge, PriorityBadge, HealthBadge } from "@/components/Badges";
import { ProgressBar } from "@/components/ProgressBar";
import CommentThread from "@/components/CommentThread";
import LateReportForm from "@/components/LateReportForm";
import DelegateDialog from "@/components/DelegateDialog";
import { formatWIB, countdown, rupiah } from "@/lib/format";
import {
  Play, Send, CheckCircle2, RotateCcw, GitFork, AlertTriangle, XCircle, FileWarning,
  ChevronRight, Clock, Target, Layers, ShieldCheck, MessageSquare, Unlock, CheckCheck, CalendarClock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function TaskDetailDrawer({ taskId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualVal, setManualVal] = useState(0);
  const [manualNote, setManualNote] = useState("");
  const [revisionNote, setRevisionNote] = useState("");
  const [showRevision, setShowRevision] = useState(false);
  const [blockerCat, setBlockerCat] = useState("");
  const [blockerDesc, setBlockerDesc] = useState("");
  const [showBlocker, setShowBlocker] = useState(false);
  const [blockerCats, setBlockerCats] = useState([]);
  const [lateOpen, setLateOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [extensions, setExtensions] = useState([]);
  const [showExt, setShowExt] = useState(false);
  const [extDeadline, setExtDeadline] = useState("");
  const [extReason, setExtReason] = useState("");

  const load = async () => {
    if (!taskId) return;
    const { data } = await api.get(`/tasks/${taskId}`);
    setTask(data);
    setManualVal(data.progress || 0);
    api.get(`/tasks/${taskId}/extensions`).then((r) => setExtensions(r.data));
  };
  useEffect(() => { if (open && taskId) load(); }, [open, taskId]);
  useEffect(() => { api.get("/blocker-categories").then((r) => setBlockerCats(r.data)); }, []);

  const refresh = async () => { await load(); onChanged?.(); };

  const doAction = async (action, note) => {
    setBusy(true);
    try {
      await api.post(`/tasks/${taskId}/status`, { action, note });
      toast.success("Status diperbarui");
      setShowRevision(false); setRevisionNote("");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Aksi gagal"); }
    setBusy(false);
  };

  const toggleCheck = async (itemId, done) => {
    try { await api.post(`/tasks/${taskId}/checklist/toggle`, { item_id: itemId, done }); await refresh(); }
    catch (e) { toast.error("Gagal memperbarui checklist"); }
  };

  const saveManual = async () => {
    if (!manualNote.trim()) return toast.error("Catatan progres wajib diisi");
    setBusy(true);
    try { await api.post(`/tasks/${taskId}/progress`, { value: manualVal, note: manualNote }); toast.success("Progres disimpan"); setManualNote(""); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    setBusy(false);
  };

  const saveBlocker = async () => {
    if (!blockerCat || !blockerDesc.trim()) return toast.error("Kategori & deskripsi wajib diisi");
    setBusy(true);
    try { await api.post(`/tasks/${taskId}/blocker`, { category: blockerCat, description: blockerDesc }); toast.success("Ditandai terkendala"); setShowBlocker(false); setBlockerDesc(""); await refresh(); }
    catch (e) { toast.error("Gagal"); }
    setBusy(false);
  };

  const unblock = async () => {
    setBusy(true);
    try { await api.post(`/tasks/${taskId}/unblock`); await refresh(); } catch (e) { toast.error("Gagal"); }
    setBusy(false);
  };

  const toIso = (v) => (v ? new Date(v + ":00+07:00").toISOString() : null);

  const submitExtension = async () => {
    if (!extDeadline || !extReason.trim()) return toast.error("Tanggal baru & alasan wajib diisi");
    setBusy(true);
    try {
      await api.post(`/tasks/${taskId}/extension`, { new_deadline: toIso(extDeadline), reason: extReason });
      toast.success("Permintaan perpanjangan dikirim"); setShowExt(false); setExtDeadline(""); setExtReason(""); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengajukan"); }
    setBusy(false);
  };

  const decideExtension = async (eid, decision) => {
    setBusy(true);
    try { await api.post(`/tasks/extension/${eid}/decide`, { decision }); toast.success("Keputusan tersimpan"); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    setBusy(false);
  };

  if (!task) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto zoa-scroll" aria-describedby={undefined}><SheetHeader className="sr-only"><SheetTitle>Memuat detail tugas</SheetTitle></SheetHeader><div className="p-8 text-slate-400">Memuat...</div></SheetContent>
      </Sheet>
    );
  }

  const me = user?.id;
  const isAdmin = ["super_admin", "owner"].includes(user?.role);
  const isAssignee = task.assignee_user_id === me;
  const isGiver = task.giver_user_id === me || isAdmin;
  const cd = countdown(task.deadline_current, task.status);
  const showLate = task.is_overdue && !task.late_report_id && task.status !== "selesai_terverifikasi";
  const isChecklistMode = ["checklist_rata", "checklist_bobot"].includes(task.progress_mode);
  const isManual = task.progress_mode === "manual";

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto zoa-scroll p-0" data-testid="task-detail-drawer" aria-describedby={undefined}>
          <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b px-6 py-4">
            {task.breadcrumb?.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-slate-400 mb-1.5 flex-wrap">
                {task.breadcrumb.map((b) => (
                  <span key={b.id} className="inline-flex items-center gap-1">{b.title}<ChevronRight className="h-3 w-3" /></span>
                ))}
                <span className="text-slate-600 font-medium">{task.title}</span>
              </div>
            )}
            <SheetHeader><SheetTitle className="text-left text-lg leading-snug">{task.title}</SheetTitle></SheetHeader>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StatusBadge status={task.status} />
              <PriorityBadge priority={task.priority} />
              <HealthBadge health={task.health} />
              <span className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-mono font-bold", cd.cls)}><Clock className="h-3 w-3" />{cd.text}</span>
            </div>
          </div>

          <div className="px-6 py-5 space-y-6">
            {showLate && isAssignee && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
                <div className="flex items-start gap-2.5">
                  <FileWarning className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-rose-800 text-sm">Tugas ini terlambat</p>
                    <p className="text-xs text-rose-600 mb-2">Wajib isi Laporan Keterlambatan. Tugas tidak dapat dikirim ke verifikasi sebelum laporan diisi.</p>
                    <Button size="sm" onClick={() => setLateOpen(true)} className="bg-rose-600 hover:bg-rose-700" data-testid="open-late-report-btn">Isi Laporan Keterlambatan</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Meta */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Meta label="Pemberi Tugas" value={task.giver?.name} />
              <Meta label="Penerima" value={task.assignee?.name || task.assignee_position} />
              <Meta label="Cabang" value={task.branch?.name} />
              <Meta label="Divisi" value={task.division?.name} />
              <Meta label="Mulai" value={formatWIB(task.start_date)} />
              <Meta label="Deadline" value={formatWIB(task.deadline_current)} />
              <Meta label="Estimasi Jam" value={`${task.estimated_hours} jam`} />
              <Meta label="Mode Progres" value={MODE_LABEL[task.progress_mode]} />
            </div>

            {task.description && <div><p className="text-xs font-semibold text-slate-400 uppercase mb-1">Deskripsi</p><p className="text-sm text-slate-700">{task.description}</p></div>}
            {task.definition_of_done && <div><p className="text-xs font-semibold text-slate-400 uppercase mb-1">Definition of Done</p><p className="text-sm text-slate-700">{task.definition_of_done}</p></div>}

            {/* Progress */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold text-slate-700">Progres {task.progress}%</span>
                <span className="text-xs text-slate-400">Ideal {task.ideal_progress}%</span>
              </div>
              <ProgressBar value={task.progress} ideal={task.ideal_progress} />
            </div>

            {/* Blocker display */}
            {task.blocker && task.status === "terkendala" && (
              <div className="rounded-xl bg-purple-50 border border-purple-200 p-3">
                <p className="text-sm font-semibold text-purple-800 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Terkendala: {task.blocker.category}</p>
                <p className="text-xs text-purple-600 mt-0.5">{task.blocker.description}</p>
                {isAssignee && <Button size="sm" variant="outline" className="mt-2" onClick={unblock}><Unlock className="h-3.5 w-3.5 mr-1" />Lepas Kendala</Button>}
              </div>
            )}

            {/* Checklist */}
            {isChecklistMode && task.checklist?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase mb-2 flex items-center gap-1"><Layers className="h-3.5 w-3.5" />Langkah / Checklist</p>
                <div className="space-y-1.5">
                  {task.checklist.map((it) => (
                    <label key={it.id} className="flex items-start gap-2.5 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid="task-checklist-item">
                      <Checkbox checked={it.done} disabled={!isAssignee} onCheckedChange={(v) => toggleCheck(it.id, !!v)} data-testid="task-checklist-item-checkbox" className="mt-0.5" />
                      <div className="flex-1">
                        <span className={cn("text-sm", it.done ? "line-through text-slate-400" : "text-slate-700")}>{it.text}</span>
                        {task.progress_mode === "checklist_bobot" && <span className="ml-2 text-xs text-indigo-500 font-semibold">({it.weight}%)</span>}
                        {it.is_approval_condition && <span className="ml-1 text-xs text-amber-600 font-semibold">• Syarat Wajib</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Manual progress */}
            {isManual && isAssignee && task.status !== "selesai_terverifikasi" && (
              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <Label>Perbarui Progres Manual: <span className="font-bold text-indigo-600">{manualVal}%</span></Label>
                <Slider value={[manualVal]} onValueChange={(v) => setManualVal(v[0])} step={5} max={100} data-testid="manual-progress-slider" />
                <Textarea placeholder="Catatan progres (wajib)" value={manualNote} onChange={(e) => setManualNote(e.target.value)} rows={2} />
                <Button size="sm" onClick={saveManual} disabled={busy} data-testid="save-manual-progress">Simpan Progres</Button>
              </div>
            )}

            {/* Subtasks */}
            {task.subtasks?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase mb-2 flex items-center gap-1"><GitFork className="h-3.5 w-3.5" />Sub-Tugas (roll-up)</p>
                <div className="space-y-2">
                  {task.subtasks.map((s) => (
                    <div key={s.id} className="rounded-lg border border-slate-200 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-700 truncate">{s.title}</span>
                        <div className="flex items-center gap-2 shrink-0"><span className="text-xs text-slate-400">bobot {s.weight}</span><StatusBadge status={s.status} /></div>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5"><ProgressBar value={s.progress} className="h-1.5" /><span className="text-xs font-semibold text-slate-500">{s.progress}%</span></div>
                      <p className="text-xs text-slate-400 mt-0.5">{s.assignee?.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Late report summary */}
            {task.late_report && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><FileWarning className="h-4 w-4" />Laporan Keterlambatan</p>
                <p className="text-xs text-amber-700 mt-1">Penyebab: {[...(task.late_report.categories||[]), ...(task.late_report.additional_categories||[])].join(", ")}</p>
                <p className="text-xs text-amber-700">Kronologi: {task.late_report.chronology}</p>
                {task.late_report.new_eta && <p className="text-xs text-amber-700">ETA baru: {formatWIB(task.late_report.new_eta)}</p>}
                <p className="text-xs mt-1 font-medium text-amber-800">Status tinjauan: {task.late_report.status}{task.late_report.fairness ? ` · ${task.late_report.fairness}` : ""}</p>
              </div>
            )}

            <Separator />

            {/* Perpanjangan Deadline */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Perpanjangan Deadline</p>
                {isAssignee && !["selesai_terverifikasi", "dibatalkan"].includes(task.status) && (
                  <Button size="sm" variant="outline" onClick={() => setShowExt((v) => !v)} data-testid="request-extension-btn">Ajukan</Button>
                )}
              </div>
              {showExt && (
                <div className="rounded-xl border border-slate-200 p-3 mb-2 space-y-2">
                  <Input type="datetime-local" value={extDeadline} onChange={(e) => setExtDeadline(e.target.value)} data-testid="extension-deadline-input" />
                  <Textarea placeholder="Alasan perpanjangan (wajib)" value={extReason} onChange={(e) => setExtReason(e.target.value)} rows={2} data-testid="extension-reason-input" />
                  <Button size="sm" onClick={submitExtension} disabled={busy} data-testid="extension-submit-btn" className="bg-indigo-600 hover:bg-indigo-700">Kirim Permintaan</Button>
                </div>
              )}
              <div className="space-y-1.5">
                {extensions.map((e) => (
                  <div key={e.id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`extension-request-${e.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-700">{formatWIB(e.old_deadline)} → <b>{formatWIB(e.new_deadline)}</b></span>
                      <span className={cn("text-xs font-bold", e.status === "disetujui" ? "text-emerald-600" : e.status === "ditolak" ? "text-rose-500" : "text-amber-600")}>{e.status === "pending" ? "Menunggu" : e.status}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{e.requested_by_name}: {e.reason}</p>
                    {isGiver && e.status === "pending" && (
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => decideExtension(e.id, "setujui")} data-testid={`extension-approve-${e.id}`}>Setujui</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600" onClick={() => decideExtension(e.id, "tolak")} data-testid={`extension-reject-${e.id}`}>Tolak</Button>
                      </div>
                    )}
                  </div>
                ))}
                {extensions.length === 0 && !showExt && <p className="text-xs text-slate-400 italic">Belum ada permintaan perpanjangan.</p>}
              </div>
            </div>

            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase">Aksi</p>
              <div className="flex flex-wrap gap-2">
                {isAssignee && task.status === "ditugaskan" && <Button size="sm" onClick={() => doAction("terima")} disabled={busy} data-testid="action-terima"><CheckCheck className="h-4 w-4 mr-1" />Terima Tugas</Button>}
                {isAssignee && task.status === "diterima" && <Button size="sm" onClick={() => doAction("mulai")} disabled={busy} data-testid="action-mulai"><Play className="h-4 w-4 mr-1" />Mulai Kerjakan</Button>}
                {isAssignee && ["sedang_dikerjakan", "revisi"].includes(task.status) && (
                  <>
                    <Button size="sm" onClick={() => doAction("kirim_verifikasi")} disabled={busy} data-testid="action-kirim-verifikasi" className="bg-sky-600 hover:bg-sky-700"><Send className="h-4 w-4 mr-1" />Kirim Verifikasi</Button>
                    <Button size="sm" variant="outline" onClick={() => setDelegateOpen(true)} data-testid="action-delegate"><GitFork className="h-4 w-4 mr-1" />Delegasikan</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowBlocker((v) => !v)} className="text-purple-600 border-purple-200" data-testid="action-terkendala"><AlertTriangle className="h-4 w-4 mr-1" />Terkendala</Button>
                  </>
                )}
                {isGiver && task.status === "menunggu_verifikasi" && (
                  <>
                    <Button size="sm" onClick={() => doAction("verifikasi_setujui")} disabled={busy} data-testid="action-verifikasi-setujui" className="bg-emerald-600 hover:bg-emerald-700"><ShieldCheck className="h-4 w-4 mr-1" />Setujui</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowRevision((v) => !v)} data-testid="action-verifikasi-revisi" className="text-amber-600 border-amber-200"><RotateCcw className="h-4 w-4 mr-1" />Minta Revisi</Button>
                  </>
                )}
                {task.status === "menunggu_lintas_divisi" && isGiver === false && (
                  <>
                    <Button size="sm" onClick={() => doAction("terima_lintas")} disabled={busy} data-testid="action-terima-lintas" className="bg-emerald-600 hover:bg-emerald-700">Terima (Lintas Divisi)</Button>
                    <Button size="sm" variant="outline" onClick={() => doAction("tolak_lintas", "Ditolak")} className="text-rose-600 border-rose-200">Tolak</Button>
                  </>
                )}
                {isGiver && !["selesai_terverifikasi", "dibatalkan"].includes(task.status) && (
                  <Button size="sm" variant="ghost" onClick={() => doAction("batal", "Dibatalkan pemberi tugas")} className="text-rose-500" data-testid="action-batal"><XCircle className="h-4 w-4 mr-1" />Batalkan</Button>
                )}
              </div>

              {showRevision && (
                <div className="flex gap-2 mt-2">
                  <Textarea placeholder="Alasan revisi (wajib)" value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} rows={2} data-testid="revision-note" />
                  <Button size="sm" onClick={() => revisionNote.trim() ? doAction("verifikasi_revisi", revisionNote) : toast.error("Alasan wajib")} className="self-end bg-amber-600 hover:bg-amber-700">Kirim</Button>
                </div>
              )}
              {showBlocker && (
                <div className="space-y-2 mt-2 rounded-lg border border-purple-200 p-3">
                  <Select value={blockerCat} onValueChange={setBlockerCat}>
                    <SelectTrigger data-testid="blocker-category"><SelectValue placeholder="Kategori hambatan" /></SelectTrigger>
                    <SelectContent>{blockerCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Textarea placeholder="Deskripsi hambatan" value={blockerDesc} onChange={(e) => setBlockerDesc(e.target.value)} rows={2} />
                  <Button size="sm" onClick={saveBlocker} className="bg-purple-600 hover:bg-purple-700">Tandai Terkendala</Button>
                </div>
              )}
            </div>

            <Separator />

            {/* Comments */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-3 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />Diskusi</p>
              <CommentThread entityType="task" entityId={task.id} />
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <LateReportForm open={lateOpen} onOpenChange={setLateOpen} taskId={taskId} onDone={refresh} />
      <DelegateDialog open={delegateOpen} onOpenChange={setDelegateOpen} task={task} onDone={refresh} />
    </>
  );
}

const MODE_LABEL = {
  checklist_rata: "Checklist Rata", checklist_bobot: "Checklist Berbobot",
  rollup_subtugas: "Roll-up Sub-tugas", manual: "Manual (Slider)", target: "Berbasis Target",
};

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-700">{value || "-"}</p>
    </div>
  );
}
